import React, { useState, useMemo, useCallback, useEffect, useContext, useRef } from 'react';
import { FaSyncAlt, FaTable, FaThLarge, FaFilter, FaFilePdf } from 'react-icons/fa';
import { useImmer } from 'use-immer';
import { useSelector } from 'react-redux';
import './kavach-dashboard.scss';

// MUI Components
import {
  Autocomplete,
  TextField,
  Chip,
  Button,
  Typography,
} from '@mui/material';

// Components
import KavachStatCards from './components/KavachStatCards';
import KavachCharts from './components/KavachCharts';
import KavachAlertPanel from './components/KavachAlertPanel';
import KavachSwitchTable from './components/KavachSwitchTable';
import KavachSwitchCards from './components/KavachSwitchCards';
import KavachAssetModal from './components/KavachAssetModal';
import KavachNetworkTopology from './components/KavachNetworkTopology';
import { AutocompleteStationListPop } from '../AutocompleteStationListPop/AutocompleteStationListPop';

// Types and Constants
import { KavachSwitch, KavachAlert, ViewMode, AvailabilityTrend } from './utils/kavachTypes';
import { calculateStats } from './utils/kavachConstants';
import { TransformedEventLogsResponse } from './utils/kavachApiTypes';

// API Service (Conventional axios method)
import { fetchKavachEventLogs } from './utils/kavachApiService';

// Context and Utils
import { ActiveAlarmsData } from '../Alarms/ActiveAlarms';
import { toast } from 'react-hot-toast';
import { getParsedLocalStorage } from '../../Utils/storage-utils';
import { extractParsedDataFromPermissions } from '../../Utils/Utils';
import { selectedCustomersSelector } from '../../features/authentication/auth-slice';

// PDF Export
import { exportSwitchesToPdf } from './utils/kavachPdfExport';

// Filter interface
interface FilterState {
  zone: any | null;
  division: any | null;
  station: any | null;
}

const initialFilters: FilterState = {
  zone: null,
  division: null,
  station: null,
};

const KavachDashboard: React.FC = () => {
  // State
  const [activeTab, setActiveTab] = useState<ViewMode>('table');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSwitch, setSelectedSwitch] = useState<KavachSwitch | null>(null);

  // API data state
  const [apiData, setApiData] = useState<TransformedEventLogsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Refs
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Filter state
  const [filters, setFilters] = useImmer<FilterState>(initialFilters);
  const [appliedFilters, setAppliedFilters] = useImmer<FilterState>(initialFilters);

  // Ref to track latest applied filters for stable loadData identity
  const appliedFiltersRef = useRef<FilterState>(appliedFilters);
  appliedFiltersRef.current = appliedFilters;

  // Context and Redux
  const { customerIdList }: any = useContext(ActiveAlarmsData);
  const selectedCustomersList = useSelector(selectedCustomersSelector);

  // Parse permissions for Zone/Division/Station dropdowns
  const user_permissions = getParsedLocalStorage('permissions', null);
  const parsedPermissions = useMemo(() => {
    return extractParsedDataFromPermissions(user_permissions);
  }, [user_permissions]);

  // Permitted customer IDs for filtering
  const permittedCustomerIds = useMemo(() => {
    return selectedCustomersList?.map((c: any) => ({ id: c.customerId, value: c.customerName })) || [];
  }, [selectedCustomersList]);

  // Zone dropdown options
  const zoneOptions = useMemo(() => {
    return parsedPermissions.zones || [];
  }, [parsedPermissions.zones]);

  // Division dropdown options - filtered by selected zones
  const divisionOptions = useMemo(() => {
    let totalDivs = parsedPermissions.divisions || [];
    if (filters.zone) {
      totalDivs = totalDivs.filter((div: any) => div.zone?.id === filters.zone.id);
    }
    return totalDivs;
  }, [parsedPermissions.divisions, filters.zone]);

  // Station dropdown options - filtered by selected zones and divisions
  const stationOptions = useMemo(() => {
    let filteredStations = (parsedPermissions.stations || []).filter((stn: any) =>
      permittedCustomerIds.find((c: any) => c.id === stn.id)
    );

    if (filters.division) {
      filteredStations = filteredStations.filter((stn: any) => stn.division?.id === filters.division.id);
    } else if (filters.zone) {
      filteredStations = filteredStations.filter((stn: any) => stn.zone?.id === filters.zone.id);
    }

    return filteredStations;
  }, [parsedPermissions.stations, permittedCustomerIds, filters.division, filters.zone]);

  // Load data function using conventional axios method
  const loadData = useCallback(async (showLoading = true, overrideFilters?: FilterState) => {
    if (showLoading) {
      setIsLoading(true);
    } else {
      setIsRefreshing(true);
    }
    setError(null);

    const activeFilters = overrideFilters || appliedFiltersRef.current;

    try {
      const data = await fetchKavachEventLogs({
        page: 0,
        size: 200,
        stn: activeFilters.station?.id
      });
      setApiData(data);
    } catch (err: any) {
      console.error('Error loading Kavach data:', err);
      setError(err.message || 'Failed to load Kavach data');
      toast.error('Failed to load Kavach event logs');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  // Initial data load
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    pollingIntervalRef.current = setInterval(() => {
      if (!isLoading && !isRefreshing) {
        loadData(false);
      }
    }, 30000);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [loadData, isLoading, isRefreshing]);

  // Refetch function for manual refresh
  const refetch = useCallback(() => {
    loadData(false);
  }, [loadData]);

  // Extract data from API response
  const switches = apiData?.switches ?? [];
  const alerts = apiData?.alerts ?? [];
  const stationStats = apiData?.stationStats ?? [];

  // Filter switches based on applied filters
  const filteredSwitches = useMemo(() => {
    let result = switches;

    // Apply station filter if station is selected (Single Selection)
    // if (appliedFilters.station) {
    //   const selectedCustomerId = appliedFilters.station.id?.toString();
    //   const selectedStationName = appliedFilters.station.value?.toUpperCase();
    //   result = result.filter((sw) => {
    //     if (sw.customerId && sw.customerId.toString() === selectedCustomerId) {
    //       return true;
    //     }
    //     return sw.station?.toUpperCase() === selectedStationName;
    //   });
    // }
    // // Apply division filter if division is selected but no station
    // else if (appliedFilters.division) {
    //   const selectedDivId = appliedFilters.division.id;
    //   const divCustomerIds = (parsedPermissions.stations || [])
    //     .filter((stn: any) => stn.division?.id === selectedDivId)
    //     .map((stn: any) => stn.id?.toString());
    //   const divStationNames = (parsedPermissions.stations || [])
    //     .filter((stn: any) => stn.division?.id === selectedDivId)
    //     .map((stn: any) => stn.value?.toUpperCase());

    //   result = result.filter((sw) => {
    //     if (sw.customerId && divCustomerIds.includes(sw.customerId.toString())) {
    //       return true;
    //     }
    //     return divStationNames.includes(sw.station?.toUpperCase());
    //   });
    // }
    // // Apply zone filter if zone is selected but no division
    // else if (appliedFilters.zone) {
    //   const selectedZoneId = appliedFilters.zone.id;
    //   const zoneCustomerIds = (parsedPermissions.stations || [])
    //     .filter((stn: any) => stn.zone?.id === selectedZoneId)
    //     .map((stn: any) => stn.id?.toString());
    //   const zoneStationNames = (parsedPermissions.stations || [])
    //     .filter((stn: any) => stn.zone?.id === selectedZoneId)
    //     .map((stn: any) => stn.value?.toUpperCase());

    //   result = result.filter((sw) => {
    //     if (sw.customerId && zoneCustomerIds.includes(sw.customerId.toString())) {
    //       return true;
    //     }
    //     return zoneStationNames.includes(sw.station?.toUpperCase());
    //   });
    // }

    return result;
  }, [switches]);

  // Get unique stations from filtered switches
  const deviceStations = useMemo(() => {
    const uniqueStations = [...new Set(filteredSwitches.map(sw => sw.station))];
    return uniqueStations.sort();
  }, [filteredSwitches]);

  // Calculate stats based on filtered switches
  const stats = useMemo(() => {
    return calculateStats(filteredSwitches);
  }, [filteredSwitches]);

  // Filter station stats (case-insensitive matching)
  const filteredStationStats = useMemo(() => {
    if (deviceStations.length === 0) {
      return stationStats;
    }
    const deviceStationsUpper = deviceStations.map(s => s?.toUpperCase());
    return stationStats.filter((s) => deviceStationsUpper.includes(s.station?.toUpperCase()));
  }, [deviceStations, stationStats]);

  // Generate availability trend from current data
  const availabilityTrend: AvailabilityTrend[] = useMemo(() => {
    const totalPorts = stats.portsUp + stats.portsDown;
    const currentAvailability = totalPorts > 0
      ? Math.round((stats.portsUp / totalPorts) * 100)
      : 0;

    const clamp = (val: number) => Math.max(0, Math.min(100, val));

    return [
      { time: '00:00', availability: clamp(currentAvailability + 2) },
      { time: '04:00', availability: clamp(currentAvailability + 1) },
      { time: '08:00', availability: clamp(currentAvailability - 1) },
      { time: '12:00', availability: clamp(currentAvailability - 2) },
      { time: '16:00', availability: clamp(currentAvailability - 1) },
      { time: '20:00', availability: clamp(currentAvailability + 1) },
      { time: 'Now', availability: currentAvailability },
    ];
  }, [stats.portsUp, stats.portsDown]);

  // Filter handlers
  const handleZoneChange = useCallback((_: any, newValue: any | null) => {
    setFilters((draft) => {
      draft.zone = newValue;
      // Clear dependent filters when zone changes
      draft.division = null;
      draft.station = null;
    });
  }, [setFilters]);

  const handleDivisionChange = useCallback((_: any, newValue: any | null) => {
    setFilters((draft) => {
      draft.division = newValue;
      // Clear station filter when division changes
      draft.station = null;
    });
  }, [setFilters]);

  const handleStationChange = useCallback((_: any, newValue: any | null) => {
    setFilters((draft) => {
      draft.station = newValue;
    });
  }, [setFilters]);

  const handleApplyFilters = useCallback(() => {
    if (!filters.station) {
      toast.error('All 3 fields (Zone, Division, Station) need to be selected.');
      return;
    }
    const newAppliedFilters = {
      zone: filters.zone,
      division: filters.division,
      station: filters.station,
    };
    setAppliedFilters(newAppliedFilters);
    // Trigger data load with the latest filters
    loadData(true, newAppliedFilters);
  }, [filters, setAppliedFilters, loadData]);

  const handleClearFilters = useCallback(() => {
    const clearedFilters = {
      zone: null,
      division: null,
      station: null,
    };
    setFilters(clearedFilters);
    setAppliedFilters(clearedFilters);
    // Reset data to full list
    loadData(true, clearedFilters);
  }, [setFilters, setAppliedFilters, loadData]);

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleExportPdf = useCallback(() => {
    if (filteredSwitches.length === 0) {
      toast.error('No data to export');
      return;
    }
    try {
      exportSwitchesToPdf(filteredSwitches, stats, filteredStationStats);
      toast.success('PDF exported successfully');
    } catch (err) {
      console.error('PDF export error:', err);
      toast.error('Failed to export PDF');
    }
  }, [filteredSwitches, stats, filteredStationStats]);

  const handleSwitchClick = useCallback((sw: KavachSwitch) => {
    setSelectedSwitch(sw);
    setIsModalOpen(true);
  }, []);

  const handleAlertClick = useCallback((alert: KavachAlert) => {
    const sw = switches.find((s) => s.id === alert.switchId);
    if (sw) {
      setSelectedSwitch(sw);
      setIsModalOpen(true);
    }
  }, [switches]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedSwitch(null);
  }, []);


  // Loading state
  if (isLoading) {
    return (
      <div className="kavach-dashboard">
        <header className="kavach-header">
          <div className="logo-container">
            <div className="kavach-logo">K</div>
            <div className="kavach-title">
              <h1>Kavach Network Monitoring System</h1>
              <div className="kavach-subtitle">
                Real-time monitoring of railway network switches
              </div>
            </div>
          </div>
          <div className="system-status">
            <div className="status-indicator">
              <div className="status-dot" style={{ backgroundColor: '#ffc107' }}></div>
              <span>Loading...</span>
            </div>
          </div>
        </header>
        <div className="loading-container" style={{ padding: '100px', textAlign: 'center' }}>
          <div className="loading-spinner" style={{ fontSize: '24px', color: '#0056b3' }}>
            Loading switch data...
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error && switches.length === 0) {
    return (
      <div className="kavach-dashboard">
        <header className="kavach-header">
          <div className="logo-container">
            <div className="kavach-logo">K</div>
            <div className="kavach-title">
              <h1>Kavach Network Monitoring System</h1>
              <div className="kavach-subtitle">
                Real-time monitoring of railway network switches
              </div>
            </div>
          </div>
          <div className="system-status">
            <div className="status-indicator">
              <div className="status-dot" style={{ backgroundColor: '#dc3545' }}></div>
              <span>Error</span>
            </div>
          </div>
        </header>
        <div className="error-container" style={{ padding: '100px', textAlign: 'center' }}>
          <div style={{ fontSize: '18px', color: '#dc3545', marginBottom: '20px' }}>
            Failed to load switch data
          </div>
          <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '20px' }}>
            {error || 'Please check your network connection'}
          </div>
          <button
            onClick={handleRefresh}
            style={{
              padding: '10px 20px',
              backgroundColor: '#0056b3',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
            }}
          >
            <FaSyncAlt style={{ marginRight: '8px' }} />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="kavach-dashboard">
      {/* Compact Header */}
      <header className="kavach-header">
        <div className="logo-container">
          <div className="kavach-logo">K</div>
          <div className="kavach-title">
            <h1>Kavach Network Monitoring System</h1>
            <div className="kavach-subtitle">
              Real-time monitoring of railway network switches
            </div>
          </div>
        </div>
        <div className="system-status">
          <div className="status-indicator">
            <div className="status-dot" style={{ backgroundColor: isRefreshing ? '#ffc107' : '#28a745' }}></div>
            <span>{isRefreshing ? 'Refreshing...' : 'Operational'}</span>
          </div>
        </div>
      </header>

      {/* Filter Section */}
      <div className="kavach-filter-section">
        <div className="filter-row">
          {/* Zone Dropdown */}
          <Autocomplete
            size="small"
            options={zoneOptions}
            getOptionLabel={(option: any) => option.value || ''}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={filters.zone}
            onChange={handleZoneChange}
            renderInput={(params) => (
              <TextField {...params} label="Zone" placeholder="Select Zone" />
            )}
            className="filter-autocomplete"
            style={{ minWidth: '200px' }}
          />

          {/* Division Dropdown */}
          <Autocomplete
            size="small"
            options={divisionOptions}
            getOptionLabel={(option: any) => option.value || ''}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={filters.division}
            onChange={handleDivisionChange}
            renderInput={(params) => (
              <TextField {...params} label="Division" placeholder="Select Division" />
            )}
            style={{ minWidth: '200px' }}
          />

          {/* Station Dropdown */}
          <Autocomplete
            size="small"
            options={stationOptions}
            getOptionLabel={(option: any) => option.value || ''}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={filters.station}
            onChange={handleStationChange}
            renderInput={(params) => (
              <TextField {...params} label="Station" placeholder="Select Station" />
            )}
            // PopperComponent={AutocompleteStationListPop}
            className="filter-autocomplete"
            style={{ minWidth: '200px' }}
          />

          {/* Filter Buttons */}
          <div className="filter-actions">
            <Button
              variant="contained"
              size="small"
              onClick={handleApplyFilters}
              disabled={isRefreshing}
              startIcon={<FaFilter />}
            >
              Apply
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleClearFilters}
              disabled={isRefreshing}
            >
              Clear
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleRefresh}
              disabled={isRefreshing}
              startIcon={<FaSyncAlt className={isRefreshing ? 'spin' : ''} />}
            >
              Refresh
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleExportPdf}
              disabled={isRefreshing || filteredSwitches.length === 0}
              startIcon={<FaFilePdf />}
              color="error"
            >
              Export PDF
            </Button>
          </div>
        </div>

        {/* Summary Info */}
        <div className="filter-summary">
          <Typography variant="body2" color="textSecondary">
            Showing <strong>{filteredSwitches.length}</strong> switches across <strong>{deviceStations.length}</strong> stations
            {appliedFilters.zone && ` in ${appliedFilters.zone.value} zone`}
            {appliedFilters.division && `, ${appliedFilters.division.value} division`}
            {appliedFilters.station && `, ${appliedFilters.station.value} station`}
          </Typography>
        </div>
      </div>

      {/* Stats Cards */}
      <KavachStatCards stats={stats} />

      {/* Charts Row - All charts in a single row on large screens */}
      <div className="charts-row">
        <KavachCharts
          stationStats={filteredStationStats}
          availabilityTrend={availabilityTrend}
          switches={filteredSwitches}
        />
      </div>

      {/* Alerts Section */}
      <div className="alerts-health-section">
        <KavachAlertPanel alerts={alerts} onAlertClick={handleAlertClick} />
      </div>

      {/* Network Topology Diagram */}
      <KavachNetworkTopology
        switches={filteredSwitches}
        onSwitchClick={handleSwitchClick}
      />

      {/* Tabs Section - Table/Card View */}
      <div className="tabs-section">
        <div className="tabs-header">
          <button
            className={`tab-button ${activeTab === 'table' ? 'active' : ''}`}
            onClick={() => setActiveTab('table')}
          >
            <FaTable />
            Table View
          </button>
          <button
            className={`tab-button ${activeTab === 'card' ? 'active' : ''}`}
            onClick={() => setActiveTab('card')}
          >
            <FaThLarge />
            Card View
          </button>
        </div>

        {/* Table View */}
        <div className={`tab-content ${activeTab === 'table' ? 'active' : ''}`}>
          <KavachSwitchTable
            switches={filteredSwitches}
            onSwitchClick={() => { }}
          // onSwitchClick={handleSwitchClick} 
          />
        </div>

        {/* Card View */}
        <div className={`tab-content ${activeTab === 'card' ? 'active' : ''}`}>
          <KavachSwitchCards switches={filteredSwitches} onSwitchClick={handleSwitchClick} />
        </div>
      </div>

      {/* Asset Details Modal */}
      <KavachAssetModal
        isOpen={isModalOpen}
        switchData={selectedSwitch}
        onClose={handleCloseModal}
      />
    </div>
  );
};

export default KavachDashboard;
